/**
 *  Stream sync management utility.
 */
class StreamSyncController {
    #timeout;

    constructor (gumboTimeStamp, player, ahead, behind, syncMode) {
        this.gumboTimeStamp = gumboTimeStamp;
        this.player = player;
        this.playerElement = null;
        this.ahead = ahead;
        this.behind = behind;
        this.videoDuration = 0;
        this.gamedayLag = null;
        this.videoLag = null;
        this.#timeout = null;
        this.syncMode = syncMode;
    }

    /**
     * Cleanup all outstanding timers and potential memory leaks.
     * @return {void}
     */
    destroy () {
        clearTimeout(this.#timeout);
        this.gumboTimeStamp = null;
        this.player = null;
        this.playerElement = null;
        this.ahead = null;
        this.behind = null;
        this.videoDuration = null;
        this.gamedayLag = null;
        this.videoLag = null;
        this.#timeout = null;
    }

    /**
     * Update the internal `gumboTimeStamp` property
     * to streamline syncing operations
     * @param  {string} newTimeStamp Gumbo formatted time string YYYYMMDD_HHMMss
     * @return {void}
     */
    updateTimeStamp (newTimeStamp) {
        this.gumboTimeStamp = newTimeStamp ?? this.gumboTimeStamp;
    }

    /**
     * Evaluate current sync conditions and take action via
     * one of the available sync strategies
     * @see #syncWithPlaybackRate
     * @see #syncWithJump
     * @return {void}
     */
    manageStream () {
        const playerElement = this.player?.current;

        if (!playerElement || !playerElement.parentNode) {
            return;
        }
        // Update some values from the refs
        this.playerElement = playerElement;

        const now = this.now();

        const videoCurrentTime = this.currentTime = playerElement?.currentTime;
        const videoDuration = this.getDuration();
        const gumboTime = (new Date(this.gumboTimeStamp)).getTime();

        this.gamedayLag = (now - gumboTime) / 1000;
        this.videoLag = videoDuration - videoCurrentTime;

        //  Helpful for sync debugging
        // console.log(`OK, so the video is ${ this.isAhead() ? 'AHEAD' : this.isBehind() ? 'BEHIND' : 'ok'}`,
        //     {
        //         willSync: (this.isAhead() || this.isBehind()),
        //         sync: (this.isAhead() ? this.gamedayLag - this.videoLag : this.videoLag - this.gamedayLag),
        //         trueDur: playerElement.duration,
        //         fakeDur: playerElement.timeline?.currentTime/1000,
        //         frakeDur: ((this.now() - this.getProgramDate())/1000),
        //         ...this.spreader()
        //     }
        // );

        if (this.syncMode === 'playback') {
            this.syncWithPlaybackRate();
        }
    }

    /**
     * Debugging helper - remove the player from the enumerable properties
     * and return the rest
     * Helps prevent memory leaks when logging and testing for OTHER memory leaks
     * @return {object} All enumerable properties of the instance minus the player.
     */
    spreader () {
        const { player, playerElement, ...otherEnumerables } = this;

        return otherEnumerables;
    }

    /**
     * Check if video is ahead of gameday and within
     * the acceptable margin `ahead`
     * @return {Boolean} stream is ahead of new gumbo timestamp
     */
    isAhead () {
        const { ahead, gamedayLag, videoLag } = this;

        return ((videoLag < (gamedayLag - ahead)));
    }

    /**
     * Check video is behind gameday and not within
     * the acceptable margin `behind`
     * @return {Boolean} stream is behind the new gumbo timestamp
     */
    isBehind () {
        const { behind, gamedayLag, videoLag } = this;

        return ((videoLag > (gamedayLag + behind)));
    }

    /**
     * Stream sync operation using HTMLVideoElement's playbackRate property
     * to speed-up/slow-down playback and close the time drift window.
     */
    syncWithPlaybackRate () {
        const { playerElement, gamedayLag, videoLag } = this;
        if (this.isAhead()) {
            // Case 1, video is AHEAD of GD
            // Pause the stream for the time diff between the two lags
            const delayMS = (gamedayLag - videoLag) * 1000;
            playerElement.playbackRate = 0.8;
            this.schedule(() => {
                if (this.playerElement) {
                    this.playerElement.playbackRate = 1;
                }
            }, delayMS / 0.8);
        } else if (this.isBehind()) {
            // Case 2, video is too far BEHIND GD
            // Jump to Live and re-eval on next func call
            const delayMS = (videoLag - gamedayLag) * 1000;

            // If we're too far behind, just jump to live.
            if (delayMS > 10000) {
                if (playerElement?.jumpToLive) {
                    playerElement.jumpToLive();
                } else if (playerElement) {
                    playerElement.currentTime = this.videoDuration - 1;
                }
            }
            playerElement.playbackRate = 1.5;
            this.schedule(() => {
                if (this.playerElement) {
                    this.playerElement.playbackRate = 1;
                }
            }, delayMS / 1.5);
        }
    }

    /**
     * Stream sync operation using HLSJS's jumpToLive and/or currentTime
     * to seek playback and close the time drift window.
     */
    syncWithJump () {
        const { playerElement, gamedayLag, videoLag, videoDuration } = this;

        if (this.isAhead()) {
            // Case 1, video is AHEAD of GD
            // Pause the stream for the time diff between the two lags
            const delayMS = (gamedayLag - videoLag) * 1000;

            playerElement.pause();
            this.schedule(() => {
                if (this.playerElement) {
                    this.playerElement.play();
                }
            }, delayMS);
        } else if (this.isBehind()) {
            // Case 2, video is too far BEHIND GD
            // Jump to Live and re-eval on next func call
            // jumpToLive only on hlsjs instances
            if (playerElement?.jumpToLive) {
                playerElement.jumpToLive();
            } else if (playerElement) {
                playerElement.currentTime = videoDuration - 1;
            }
        }
    }

    /**
     * Schedule an operation to run in `ms` milliseconds.
     * Only one queued operation permitted.
     * @param  {function} op Operation to be run in `ms` milliseconds.
     * @param  {number} ms Time to wait in milliseconds before running `op`
     * @return {void}
     */
    schedule (op, ms) {
        clearTimeout(this.#timeout);
        this.#timeout = setTimeout(op, ms);
    }

    /**
     * Retrieve and/or synthesize a valid stream duration.
     * If not provided by the player, attempt to create one using the X-PROGRAM-DATE field
     * from the hls manifest file.
     * @see #getProgramDate
     * @return {number}  Current stream duration in seconds.
     */
    getDuration () {
        const { playerElement } = this;
        const videoCurrentTime = playerElement?.currentTime;
        let videoDuration = playerElement?.duration;
        let videoDurationSync = videoCurrentTime;

        // Native hls live on safari sets the duration to infinity
        // HJSJS is configured to give a constructed duration
        if (videoDuration === Infinity) {
            // Create a timeline object and stash it on the video element
            // using programDateTime and DocumentTimeline to keep a rolling
            // playbackSpeed agnostic measure of the current video duration
            if (playerElement && !playerElement?.timeline) {
                const programDate = this.getProgramDate();
                const programTimeDuration = new DocumentTimeline({ originTime: (this.now() - programDate) }); // attempt an offset of programTime to video start.
                playerElement.timeline = programTimeDuration;
            }

            const { currentTime: timelineCurrentTime } = playerElement?.timeline ?? {};

            if (timelineCurrentTime ?? false) {
                videoDurationSync = timelineCurrentTime;
            }
            videoDuration = timelineCurrentTime; // calc duration as programTime + videoCurrentTime
        }
        return this.videoDuration = videoDuration;
    }

    /**
     * Now
     * @return {number} ms since epoch
     */
    now () {
        return Date.now();
    }

    /**
     * Retrieve the stored `gumboTimeStamp
     * @return {string | null} Gumbo timestamp string or null if not set.
     */
    getTimeStamp () {
        return this.gumboTimeStamp;
    }

    /**
     * Read the X-PROGRAM-DATE as provided by the hls manifest.
     * If not present, attempt to create one using the current time
     * @note Manifest cache and chunk size should mean the program date is within
     *       2s of the inital manifest pull.
     * @return {number} X-PROGRAM-DATE in milliseconds from epoch
     */
    getProgramDate () {
        const playerElement = this.playerElement ?? this.player?.current;

        let programDate = 0;

        if (playerElement?.getStartDate) {
            programDate = (new Date(playerElement.getStartDate())).getTime();
        } else if (playerElement) {
            programDate = this.fakeProgram = this.fakeProgram ?? this.now();
        }

        return programDate;
    }

    /**
    * Seek video to the `playEndTime`
    * @param {string} playEndTime - The timestamp to seek to (ISO 8601 format or seconds)
    */

    seekToTimeStamp (fragments) {
        if (fragments?.length === 0) {
            return;
        }

        const { playerElement } = this;
        const targetTimestamp = new Date(this.gumboTimeStamp).getTime();
        let seekPosition = 0;
        let found = false;
        for (let i = fragments?.length - 1; i >= 0; i--) {
            const segment = fragments[i];
            const segmentTime = new Date(segment.programDateTime).getTime();
            const segmentEndTime = segmentTime + segment.duration * 1000;
            if (targetTimestamp >= segmentTime && targetTimestamp <= segmentEndTime) {
                seekPosition = segment.start + (targetTimestamp - segmentTime) / 1000;
                found = true;
                break;
            }
        }
        if (found) {
            playerElement.currentTime = seekPosition;
        }
    }
};

export default StreamSyncController;
